import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpeg: FFmpeg | null = null;

export async function loadFFmpeg(): Promise<FFmpeg> {
  if (ffmpeg) return ffmpeg;

  ffmpeg = new FFmpeg();
  
  // Load core files from CDN
  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
  
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
  });

  return ffmpeg;
}

export async function cutVideo(
  videoFile: File,
  startTime: number,
  duration: number,
  aspectRatio: '9:16' | '1:1' | '16:9' = '9:16'
): Promise<Blob> {
  const ffmpeg = await loadFFmpeg();
  
  // Write input file to FFmpeg virtual filesystem
  await ffmpeg.writeFile('input.mp4', await fetchFile(videoFile));

  // Build FFmpeg command based on aspect ratio
  let filter = '';
  switch (aspectRatio) {
    case '9:16':
      filter = 'crop=ih*9/16:ih,scale=1080:1920';
      break;
    case '1:1':
      filter = 'crop=min(iw\\,ih):min(iw\\,ih),scale=1080:1080';
      break;
    case '16:9':
      filter = 'crop=ih*16/9:ih,scale=1920:1080';
      break;
  }

  // Execute FFmpeg command
  await ffmpeg.exec([
    '-i', 'input.mp4',
    '-ss', startTime.toString(),
    '-t', duration.toString(),
    '-vf', filter,
    '-c:v', 'libx264',
    '-preset', 'fast',
    '-crf', '23',
    '-c:a', 'aac',
    '-b:a', '128k',
    '-movflags', '+faststart',
    'output.mp4'
  ]);

  // Read output file
  const data = await ffmpeg.readFile('output.mp4');
  
  // Cleanup
  await ffmpeg.deleteFile('input.mp4');
  await ffmpeg.deleteFile('output.mp4');

  // Convert to Blob
  const uint8Array = data as Uint8Array;
  const arrayBuffer = new Uint8Array(uint8Array).buffer;
  return new Blob([arrayBuffer], { type: 'video/mp4' });
}

export async function extractAudio(videoFile: File, onProgress?: (progress: number) => void): Promise<Blob> {
  console.log(`Extracting audio from: ${videoFile.name} (${(videoFile.size / 1024 / 1024).toFixed(1)}MB)`);
  
  // Try Web Audio API first (10x faster - uses hardware decoder)
  try {
    if (onProgress) onProgress(10);
    
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await videoFile.arrayBuffer();
    
    if (onProgress) onProgress(30);
    
    // Decode video to audio - browser's native decoder (very fast)
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    
    if (onProgress) onProgress(50);
    
    // Resample to 16kHz mono for Whisper using OfflineAudioContext
    const sampleRate = 16000;
    const numChannels = 1;
    const length = Math.floor(audioBuffer.duration * sampleRate);
    const offlineCtx = new OfflineAudioContext(numChannels, length, sampleRate);
    
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start(0);
    
    if (onProgress) onProgress(70);
    
    const resampled = await offlineCtx.startRendering();
    const audioData = resampled.getChannelData(0);
    
    if (onProgress) onProgress(90);
    
    console.log(`Audio extracted (Web Audio API): ${audioBuffer.duration.toFixed(1)}s, resampled to 16kHz mono`);
    
    // Convert Float32Array to WAV Blob
    const wavBlob = float32ToWav(audioData, sampleRate);
    
    await audioCtx.close();
    
    if (onProgress) onProgress(100);
    
    console.log(`WAV created: ${(wavBlob.size / 1024 / 1024).toFixed(1)}MB`);
    return wavBlob;
    
  } catch (error) {
    // Fallback to FFmpeg if Web Audio API fails
    console.warn('Web Audio API failed, falling back to FFmpeg:', error);
    return extractAudioFFmpeg(videoFile, onProgress);
  }
}

// Convert Float32Array to WAV Blob
function float32ToWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  
  // WAV header
  view.setUint32(0, 0x52494646, false); // "RIFF"
  view.setUint32(4, 36 + samples.length * 2, true);
  view.setUint32(8, 0x57415645, false); // "WAVE"
  view.setUint32(12, 0x666d7420, false); // "fmt "
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  view.setUint32(36, 0x64617461, false); // "data"
  view.setUint32(40, samples.length * 2, true);
  
  // Convert Float32 to Int16
  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  
  return new Blob([buffer], { type: 'audio/wav' });
}

// Fallback: FFmpeg extraction (slow but compatible)
async function extractAudioFFmpeg(videoFile: File, onProgress?: (progress: number) => void): Promise<Blob> {
  const ffmpeg = await loadFFmpeg();
  
  console.log(`Extracting audio from: ${videoFile.name} (${(videoFile.size / 1024 / 1024).toFixed(1)}MB)`);
  
  if (onProgress) onProgress(10);
  
  await ffmpeg.writeFile('input.mp4', await fetchFile(videoFile));

  if (onProgress) onProgress(30);

  // Set up progress listener for FFmpeg
  if (onProgress) {
    ffmpeg.on('progress', ({ progress }: { progress: number }) => {
      // progress is 0 to 1, map to 30-100 range
      onProgress(30 + progress * 70);
    });
  }

  const exitCode = await ffmpeg.exec([
    '-i', 'input.mp4',
    '-vn',
    '-acodec', 'pcm_s16le',
    '-ar', '16000',
    '-ac', '1',
    'output.wav'
  ]);

  if (exitCode !== 0) {
    await ffmpeg.deleteFile('input.mp4').catch(() => {});
    throw new Error(`FFmpeg audio extraction failed (exit code: ${exitCode})`);
  }

  if (onProgress) onProgress(100);

  const data = await ffmpeg.readFile('output.wav');
  
  await ffmpeg.deleteFile('input.mp4').catch(() => {});
  await ffmpeg.deleteFile('output.wav').catch(() => {});

  const uint8Array = data as Uint8Array;
  console.log(`Audio extracted: ${(uint8Array.length / 1024 / 1024).toFixed(1)}MB WAV`);
  const arrayBuffer = new Uint8Array(uint8Array).buffer;
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

export async function getVideoInfo(videoFile: File): Promise<{
  duration: number;
  width: number;
  height: number;
}> {
  const ffmpeg = await loadFFmpeg();
  
  await ffmpeg.writeFile('input.mp4', await fetchFile(videoFile));

  // Use ffprobe to get video info
  await ffmpeg.exec([
    '-i', 'input.mp4',
    '-f', 'null',
    '-'
  ]);

  // Parse output (simplified - in production, use ffprobe)
  // For now, return mock data
  await ffmpeg.deleteFile('input.mp4');

  return {
    duration: 0,
    width: 0,
    height: 0
  };
}
